import styled from '@emotion/styled'
import * as Popover from '@radix-ui/react-popover'
import { FC, ReactNode } from 'react'

import { Dayjs } from '@bemlo/date'
import { OnCallType } from '@bemlo/enums'
import { Enumerable } from '@bemlo/utils'

import { CellStatus, useCellTheme } from './useCellTheme'
import { Color, theme } from '../../../../constants'
import { Intent } from '../../../../constants/intent'
import {
  Badge,
  Checkbox,
  Clickable,
  Div,
  Flex,
  Icon,
  Text,
} from '../../../../elements'
import { TooltipIcon } from '../../../../molecules/TooltipIcon'
import { useTranslations } from '../../../../translate'
import { ShiftCrossSection } from '../../ShiftCrossSection'
import { ScheduleZoom } from '../types'
import { useSchedule, useSelectedShifts } from '../useSchedule'

const RelativeWrapper = styled(Div)<{ fullHeight: boolean }>(
  ({ fullHeight }) => ({
    position: 'relative',
    width: '100%',
    height: fullHeight ? '100%' : 'auto',
  }),
)

const ShiftBadge = styled(Div)<{ background: Color }>(({ background }) => ({
  background,
  borderRadius: theme.border.radius.normal,
  position: 'absolute',
  top: `-${theme.border.width.thin}px`,
  right: `-${theme.border.width.thin}px`,
  fontWeight: theme.font.weight.book,
  padding: `${theme.spacing['0.5']}px ${theme.spacing['1.5']}px`,
  lineHeight: 1,
}))

const Cell = styled(Div)<{
  background: Color
  hoverBackground?: Color
  opacity?: number
  border?: string
  hoverable: boolean
  disabled: boolean
  selected?: boolean
  isUrgent?: boolean
  fullHeight: boolean
  zoom: ScheduleZoom
}>(
  ({
    background,
    hoverBackground,
    border,
    hoverable,
    disabled,
    opacity,
    selected,
    isUrgent,
    zoom,
    fullHeight,
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    width: '100%',
    position: 'relative',
    padding: `${theme.spacing[1.5]}px ${theme.spacing[2]}px`,
    background,
    opacity,
    border: border ?? `1px solid ${background}`,
    borderRadius: theme.border.radius.normal,
    textAlign: 'start',
    ...(disabled && { cursor: 'not-allowed' }),
    ...(isUrgent && {
      border: `1px solid ${Color.RED}`,
      borderLeft: zoom !== 'month' ? `19px solid ${Color.RED}` : undefined,
    }),
    ...(fullHeight && {
      height: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    }),

    '.checkbox': {
      display: selected ? 'block' : 'none',
    },

    ...(hoverable
      ? {
          cursor: 'pointer',
          ':hover': {
            filter: 'brightness(0.95)',
            ...(hoverBackground && { background: hoverBackground }),
            '.checkbox': {
              display: 'block',
            },
          },
        }
      : {
          ':hover': {
            '.checkbox': {
              display: 'block',
            },
          },
        }),
  }),
)

export const PopoverContentWrapper = styled(Popover.Content)({
  zIndex: theme.zIndex.popover,
})

type ShiftProps = {
  shiftIds: string[]
  status: CellStatus
  title?: string
  startTime: string
  endTime: string
  popoverContent?: FC<{ shiftId: Enumerable<string> }>
  unselectable?: boolean
  disabled?: boolean
  isAcknowledged?: boolean
  isUrgent?: boolean
  onDelete?: () => void
  onClick?: (shiftId: Enumerable<string>) => void
  onCallBadge?: { type: OnCallType } | null
  error?: ReactNode
  tooltip?: { message: string; intent: Intent }
  interestedCandidates?: number
  stretch?: boolean
  onCallShift?:
    | undefined
    | {
        startDateTime: Dayjs
        endDateTime: Dayjs
        onCallTime?: {
          startDateTime: Dayjs
          endDateTime: Dayjs
        } | null
      }
}

export const Shift = ({
  title,
  interestedCandidates = 0,
  shiftIds,
  startTime,
  endTime,
  status,
  isAcknowledged,
  onDelete,
  popoverContent: PopoverContent,
  tooltip,
  disabled = false,
  unselectable,
  error,
  isUrgent,
  onClick,
  onCallBadge,
  onCallShift,
  stretch,
}: ShiftProps) => {
  const t = useTranslations()
  const { zoom } = useSchedule()
  const { isSelected, toggleSelected, enableSelectShifts } =
    useSelectedShifts(shiftIds)

  const {
    background,
    hoverBackground,
    border,
    label,
    color,
    subtitleOpacity,
    opacity,
    icon,
    badgeColor,
    iconProps,
  } = useCellTheme()[status]

  const fullHeight = zoom === 'month' && !!stretch

  const countBadge =
    shiftIds.length > 1 ? (
      <ShiftBadge background={Color.LIGHTER_BLUE}>
        <Text
          display="block"
          color={Color.BLUE}
          fontSize={theme.font.size['6.5']}
        >
          ×{shiftIds.length}
        </Text>
      </ShiftBadge>
    ) : null

  const interestedBadge = (
    <ShiftBadge background={Color.BLUE}>
      <Text
        bold
        color={Color.WHITE}
        display="block"
        fontSize={theme.font.size['6.5']}
        letterSpacing={'-2px'}
      >
        {interestedCandidates} / {shiftIds.length}
      </Text>
    </ShiftBadge>
  )

  const deleteBadge = (
    <ShiftBadge background={Color.TRANSPARENT}>
      <Clickable onClick={onDelete} styles={{ py: 0.5 }}>
        <Icon size="sm" name="TrashIcon" color={Color.MEDIUM_RED} />
      </Clickable>
    </ShiftBadge>
  )

  // TODO: refactor this to show on hover/selected and add "can select" prop
  const selectBadge = (
    // styling here could benefit from other approaches, but that might require larger changes
    <ShiftBadge
      className="checkbox"
      styles={{ display: 'table' }}
      onClick={(e) => {
        // so we do not open the popover
        e.stopPropagation()
      }}
      background={Color.TRANSPARENT}
    >
      <Checkbox
        styles={{ marginTop: 3 }}
        size="sm"
        checked={isSelected}
        onChange={toggleSelected}
      />
    </ShiftBadge>
  )

  const labelNode = (
    <Flex gap={1}>
      <Text
        fontSize={theme.font.size['6.5']}
        color={color}
        bold
        display="block"
      >
        {title ?? label}
      </Text>
      {isAcknowledged && (
        <TooltipIcon
          color={Color.WHITE}
          size="sm"
          iconName="EyeIcon"
          text={t.ui.shift_acknowledged_by_candidate()}
          backgroundColor={Color.TRANSPARENT}
        />
      )}
    </Flex>
  )

  const urgentLabel = (
    <TooltipIcon
      size="sm"
      styles={{
        position: 'absolute',
        left: -17,
        top: 14,
      }}
      color={Color.LIGHTER_RED}
      backgroundColor={Color.TRANSPARENT}
      design="solid"
      iconName="ExclamationIcon"
      tooltipIntent="warning"
      text={t.shifts.urgent()}
    />
  )

  const clickable = !!onClick || !!PopoverContent

  const cellContent = (
    <>
      {icon ? (
        <Flex gap={1}>
          {labelNode}
          <Icon name={icon} size="sm" color={color} design="solid" />
        </Flex>
      ) : (
        labelNode
      )}
      <Text
        mt={0.5}
        fontSize={theme.font.size['6.5']}
        color={color}
        opacity={subtitleOpacity}
        display="block"
        styles={{ letterSpacing: '-0.5px' }}
      >
        {startTime} – {endTime}
        {onCallBadge && (
          <Badge squared size="xxs" color={badgeColor} styles={{ ml: 1 }}>
            {t.$onCallTypeAbbreviation(onCallBadge.type)}
          </Badge>
        )}
      </Text>
      {onCallShift && <ShiftCrossSection size="sm" {...onCallShift} />}
      {interestedCandidates ? interestedBadge : countBadge}
      {onDelete && deleteBadge}
      {!unselectable && enableSelectShifts && selectBadge}
      {isUrgent && urgentLabel}
    </>
  )

  const cell = (
    <Cell
      disabled={disabled}
      as={clickable ? 'button' : 'div'}
      background={background}
      hoverBackground={hoverBackground}
      onClick={onClick ? () => onClick(shiftIds) : undefined}
      border={border}
      opacity={opacity}
      selected={isSelected}
      hoverable={!!clickable}
      isUrgent={isUrgent}
      zoom={zoom}
      fullHeight={fullHeight}
    >
      {zoom !== 'month' ? (
        cellContent
      ) : (
        <>
          <Icon name={iconProps.name} design="solid" color={color} />
          {interestedCandidates ? interestedBadge : countBadge}
        </>
      )}
    </Cell>
  )

  const withTooltip = tooltip ? (
    <RelativeWrapper fullHeight={fullHeight}>
      {tooltip && (
        <TooltipIcon
          text={tooltip.message}
          tooltipIntent={tooltip.intent}
          styles={{
            zIndex: theme.zIndex.tooltip,
            position: 'absolute',
            top: -theme.spacing[2],
            right: -theme.spacing[2],
          }}
        />
      )}
      {cell}
    </RelativeWrapper>
  ) : error ? (
    <RelativeWrapper fullHeight={fullHeight}>
      {cell}
      {error}
    </RelativeWrapper>
  ) : (
    cell
  )

  if (!PopoverContent) return withTooltip

  return (
    <Popover.Root>
      <Popover.Trigger asChild>{withTooltip}</Popover.Trigger>
      <PopoverContentWrapper
        sideOffset={4}
        align="start"
        side="right"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <PopoverContent shiftId={shiftIds} />
      </PopoverContentWrapper>
    </Popover.Root>
  )
}
